import { catchError, forkJoin, of, switchMap, tap, filter, map, distinctUntilChanged, concat } from 'rxjs';
import { ofType } from 'redux-observable';
import messageService from 'layout/services/messageService';
import uiStateSlice from 'layout/redux/uiStateSlice';
import LoadingStatus from 'enums/loadingStatus';
import { searchParametersActions, selectMarketId } from 'features/searchResultsFacets';
import { selectLayoutBreakpoint } from 'layout/redux/selectors';
import LayoutBreakpoint from 'layout/enums/layoutBreakpoint';
import getMarkersLoaderEndpoints from '../services/markersEndpointSelector';
import { mapStateSlice, interactiveSitePlanSlice } from './slices';
import { 
  selectedLotSummaryCard,
  selectSummaryCardsStatus,
  selectSummaryCards,
} from './selectors';
import getCommunitySummaryCard from '../apis/getCommunitySummaryCard';
import getHousingUnitSummaryCardInformation from '../apis/getHousingUnitSummaryCard';
import getPlanSummaryCardInformation from '../apis/getPlanSummaryCard';
import MarkerType from '../enums/markerType';
import getBuildablePlanSummaryCardsInformation from '../apis/getBuildablePlanSummaryCards';
import getMasterPlannedCommunitySummaryCardInformation from '../apis/getMasterPlannedCommunitySummaryCard';

const actions = {
  ...mapStateSlice.actions,
  ...uiStateSlice.actions,
  ...interactiveSitePlanSlice.actions,
};

export default (action$) =>
action$.pipe(
  ofType(actions.fetchResults.type),
  switchMap((action) => {
    const apiRequests = getMarkersLoaderEndpoints(action.payload.parameters);
    return forkJoin(apiRequests).pipe(
      switchMap((data) => of(actions.fetchResultsFulfilled({ parameters: action.payload.parameters, ...data }))),
      tap((data) => {
        messageService.sendMessage({
          messageType: 'mapBoundaries',
          payload: {
            mapBoundaries: data.payload.communities?.MapBoundaries || data.payload.homes?.MapBoundaries,
          },
        });
      }),
      catchError((err) => of(actions.fetchResultsError(err)))
    );
  })
);

/*
 * Epic to refresh the page number of each marker when the sort event o pagesize event is raised. 
 * Is those events ths listing is refreshed, but the marker not.
 */
export const updateMarkersPageNumberEpic = (action$, state$) =>
  action$.pipe(
    ofType(
      searchParametersActions.fetchSort.type,
      searchParametersActions.fetchPageSize.type,
    ),
    // When the Layout is small the page numbers shouldn't be updated 
    filter(() => selectLayoutBreakpoint(state$.value).getValue() !== LayoutBreakpoint.Small.getValue()),
    switchMap(() => {
      const parameters = state$.value.searchParameters;
      const apiRequests = getMarkersLoaderEndpoints(parameters);
      return forkJoin(apiRequests).pipe(
        switchMap((data) => of(actions.fetchResultsPagesNumbers({ parameters, ...data }))),
      );
    })
  );

/**
 * Epic that clears the summary card when the search parameters change, 
 * the search within map is disabled, or the search within map is enabled.
 * @param {Observable} action$ - Observable stream of actions.
 * @returns {Observable} - Observable stream of actions.
 */
export const clearSummaryCardsOnMapChangesEpic = (action$) =>
  action$.pipe(
    ofType(
      actions.disableSearchWithinMap.type,
      actions.enableSearchWithinMap.type
    ),
    map(selectSummaryCards),
    filter((summaryCard) => Boolean(summaryCard)),
    switchMap(() => of(actions.clearSummaryCards()))
  );

  /**
 * Epic for retrieving additional information for a community marker.
 * @param {Observable} action$ - Observable stream of actions.
 * @returns {Observable} - Observable stream of actions.
 */
export const getCommunitySummaryCardEpic = (action$, state$) =>
  action$.pipe(
    ofType(actions.selectedMarker.type),
    filter(() => selectLayoutBreakpoint(state$.value).text === LayoutBreakpoint.Small.toString()),
    filter((action) =>
      action.payload?.type === MarkerType.HousingDevelopment.toString()
    ),
    switchMap((action) => {
      const marketId = selectMarketId(state$.value);
      const communityId = action.payload.metaData.communityId;
      const isMasterPlannedCommunity = action.payload?.metaData?.isMasterPlannedCommunity;

      const apiCall$ = isMasterPlannedCommunity
        ? getMasterPlannedCommunitySummaryCardInformation(communityId, marketId)
        : getCommunitySummaryCard(communityId, marketId);

      return concat(
        of(actions.updateSummaryCardsStatus(LoadingStatus.Loading.toString())),
        apiCall$.pipe(
          map((data) => actions.getSummaryCardsFulfilled([data])),
          catchError((error) => of(actions.getSummaryCardsFailed(error)))
        )
      );
    })
  );

/**
 * Epic for retrieving additional marker information for a plan marker.
 *
 * @param {Observable} action$ - The stream of actions.
 * @returns {Observable} Observable stream of actions
 */
export const getPlanSummaryCardEpic = (action$, state$) =>
  action$.pipe(
    ofType(actions.selectedMarker.type),
    filter(() => selectLayoutBreakpoint(state$.value).text === LayoutBreakpoint.Small.toString()),
    filter((action) => action.payload?.type === MarkerType.PlanUnit.toString()),
    switchMap((action) => {
      const marketId = selectMarketId(state$.value);
      const communityId = action.payload.metaData.communityId;
      const planId = action.payload.metaData.id;

      return concat(
        of(actions.updateSummaryCardsStatus(LoadingStatus.Loading.toString())),
        getPlanSummaryCardInformation(
          planId,
          communityId,
          marketId
        ).pipe(
          map((data) => actions.getSummaryCardsFulfilled([data])),
          catchError((error) => of(actions.getSummaryCardsFailed(error)))
        )
      );
    })
  );

/**
 * Epic for retrieving additional marker information for a plan marker.
 *
 * @param {Observable} action$ - The stream of actions.
 * @returns {Observable} Observable stream of actions
 */
export const getBuildablePlanSummaryCardEpic = (action$, state$) =>
  action$.pipe(
    ofType(actions.selectedMarker.type),
    filter(() => selectLayoutBreakpoint(state$.value).text === LayoutBreakpoint.Small.toString()),
    filter((action) => action.payload?.type === MarkerType.BuildablePlan.toString()),
    switchMap((action) => {
      const marketId = selectMarketId(state$.value);
      const communityId = action.payload.metaData.communityId;

      return concat(
        of(actions.updateSummaryCardsStatus(LoadingStatus.Loading.toString())),
        getBuildablePlanSummaryCardsInformation(
          communityId,
          marketId
        ).pipe(
          map((data) => {
            const { community, plans } = data;
            const allListings = [community, ...plans.flat()];
            return actions.getSummaryCardsFulfilled(allListings);
          }),
          catchError((error) => of(actions.getSummaryCardsFailed(error)))
        )
      );
    })
  );

/**
 * Epic for retrieving additional marker information for a spec.
 *
 * @param {Observable} action$ - The stream of actions.
 * @returns {Observable} The stream of actions for retrieving additional marker information.
 */
export const getSpecSummaryCardEpic = (action$, state$) =>
  action$.pipe(
    ofType(actions.selectedMarker.type),
    filter(() => selectLayoutBreakpoint(state$.value).text === LayoutBreakpoint.Small.toString()),
    filter((action) => action.payload?.type === MarkerType.SpecUnit.toString()),
    switchMap((action) => {
      const marketId = selectMarketId(state$.value);
      const communityId = action.payload.metaData.communityId;
      const specId = action.payload.metaData.id;

      return concat(
        of(actions.updateSummaryCardsStatus(LoadingStatus.Loading.toString())),
        getHousingUnitSummaryCardInformation(
          specId,
          communityId,
          marketId
        ).pipe(
          map((data) => actions.getSummaryCardsFulfilled([data])),
          catchError((error) => of(actions.getSummaryCardsFailed(error)))
        )
      );
    })
  );

  /**
   * Clears the summary card information when a listing is focused.
   *
   * @param {Observable} action$ - The stream of actions.
   * @returns {Observable} The stream of actions to be dispatched.
   */
  export const clearSummaryCardsEpic = (action$, state$) =>
  action$.pipe(
    ofType(actions.focusedListingInformation.type, searchParametersActions.fetchResultsType.type),
    map(selectSummaryCardsStatus),
    filter((status) => {
      const summaryCards = selectSummaryCards(state$.value);

      return summaryCards?.length > 0 && status !== LoadingStatus.Idle.toString();
    }),
    switchMap(() => of(actions.clearSummaryCards()))
  )

  /**
   * Removes the focused listing information when the summary card closes.
   * 
   * @param {Observable} action$ - The stream of actions.
   * @returns {Observable} The stream of actions to be dispatched.
   */
  export const clearFocusedListingOnSummaryCardClosedEpic = (action$) =>
    action$.pipe(
      ofType(
        actions.clearSummaryCards.type,
      ),
      switchMap(() => of(actions.focusedListingInformation(null)))
    );

/**
 * fetchSummaryCardForInteractiveSitePlanEpic epic is meant to
 * retrieve a summary card after an interactive site plan
 * lot is selected by the user.
 * @returns {Observable} The stream of actions to be dispatched.
 */
export const fetchSummaryCardForInteractiveSitePlanEpic = (action$, state$) =>
  action$.pipe(
    ofType(actions.setLotInformation.type),
    map((action) => ({
      communityId: action.payload?.communityId,
      specId: action.payload?.availableSpec,
    })),
    distinctUntilChanged((previous, current) => {
      if (!selectedLotSummaryCard(state$.value)) {
        return false;
      }

      return previous.communityId === current.communityId &&
        previous.specId === current.specId;
    }),
    filter(({ communityId, specId }) => Boolean(communityId) || Boolean(specId)),
    switchMap(({ communityId, specId }) => {
      const marketId = selectMarketId(state$.value);

      const getSummaryCard$ = specId
        ? getHousingUnitSummaryCardInformation(specId, communityId, marketId)
        : getCommunitySummaryCard(communityId, marketId);

      return getSummaryCard$.pipe(
        switchMap((data) => of(actions.getInteractiveSitePlanCardFulfilled(data))),
        catchError((error) => of(actions.getInteractiveSitePlanCardFailed(error)))
      );
    })
  );

/**
 * removeInteractiveSitePlanEpic will react to community maps removed in order to remove
 * the interactive site plan information related to them, if any.
 * @param {Observable} action$ - The stream of actions.
 * @returns {Observable} The stream of actions to be dispatched.
 */
export const removeInteractiveSitePlanEpic = (action$) =>
  action$.pipe(
    ofType(actions.removeCommunityMap.type),
    map((action) => actions.removeInteractiveSitePlan(action.payload))
  );